import isEmpty from "lodash/isEmpty";
import {
  CustomFieldsValue,
  Payee,
  PayeeEngagementType,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { payeeEngagementsApi, payeesApi } from "@api";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesPayees
} from "@query";
import { selectorCounterpartyProfileWrite } from "../selectors/selectorCounterpartyProfileWrite";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export type CreatePayeeInput = {
  email: string;
  name?: string;
  company?: string;
  externalId?: string;
  /**
   * Custom-field values, keyed and typed per their definitions. v3 `PayeeCreate`
   * has no home for them, so they are persisted after create via
   * `updatePayeeCustomFields`; skipped when empty.
   */
  customFields?: { [key: string]: CustomFieldsValue };
  /**
   * Payer-owned data (the QBO integration mapping) that v3 `PayeeCreate` has no
   * home for — persisted after create via the per-payee `updatePayee` fields;
   * skipped when empty.
   */
  payerOwnedData?: {
    integration?: {
      quickbooks?: { vendorId?: string; expenseAccountId?: string };
    };
  };
  /**
   * Engagements to assign after create, via v3 `createPayeeEngagement`. Each
   * carries its `engagementType` (from the v3 `Engagement`), which the v3
   * assignment requires and v1 never sent. Assigned sequentially; omit for the
   * bulk flow, which loops engagements itself with per-item progress.
   */
  engagements?: Array<{ id: string; engagementType: PayeeEngagementType }>;
};

const invitePayeeIgnoringAlreadyInvited = async (request: {
  payeeId: string;
  xWingspanAccount?: string;
}): Promise<void> => {
  try {
    await payeesApi.invitePayee(request);
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 409) {
      return;
    }
    throw error;
  }
};

/**
 * Creates a contractor (payee) on v3 and issues the Wingspan invite.
 *
 * v1 `createPayee` was one fat call that created the payee, invited them, and
 * persisted payer-owned data (custom fields, QBO mapping) in a single request.
 * v3 splits these: `createPayee` takes the lean payee (email / externalId /
 * profile); the invite (implicit in v1) is an explicit `invitePayee`; custom
 * fields are persisted via the dedicated v3 `updatePayeeCustomFields`; and the
 * QBO vendor / expense-account mapping via the per-payee v3 fields on
 * `updatePayee` (`quickbooksVendorId` / `quickbooksExpenseAccountId`).
 * See V3_MIGRATION_TRACKER.md.
 *
 * Org/expansion context: v1's payer-scoped calls used the `X-Wingspan-User`
 * header; the v3 equivalent is the `xWingspanAccount` request scope (relies on
 * the D1/D24 `userId == accountId` equivalence). In the common (non-org) path
 * `useXWingspanPayerId()` is `undefined`, so no scope override is sent.
 */
export const useCreatePayee = (
  config?: WSMutationConfig<Payee, WSServiceError, CreatePayeeInput>
) => {
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<Payee, WSServiceError, CreatePayeeInput>(
    async input => {
      const payee = await payeesApi.createPayee({
        payeeCreate: {
          email: input.email,
          externalId: input.externalId || undefined,
          profile: selectorCounterpartyProfileWrite({
            name: input.name,
            company: input.company
          })
        },
        xWingspanAccount
      });

      const payeeId = payee.id!;

      const customFields = input.customFields;
      // QBO vendor / expense-account mapping → per-payee v3 fields.
      const quickbooks = input.payerOwnedData?.integration?.quickbooks;

      await Promise.all([
        invitePayeeIgnoringAlreadyInvited({ payeeId, xWingspanAccount }),

        customFields && !isEmpty(customFields)
          ? payeesApi.updatePayeeCustomFields({
              payeeId,
              requestBody: customFields,
              xWingspanAccount
            })
          : undefined,

        quickbooks && !isEmpty(quickbooks)
          ? payeesApi.updatePayee({
              payeeId,
              payeeUpdate: {
                quickbooksVendorId: quickbooks.vendorId,
                quickbooksExpenseAccountId: quickbooks.expenseAccountId
              },
              xWingspanAccount
            })
          : undefined,

        ...(input.engagements ?? []).map(engagement =>
          payeeEngagementsApi.createPayeeEngagement({
            payeeId,
            payeeEngagementCreate: {
              engagementId: engagement.id,
              engagementType: engagement.engagementType
            },
            xWingspanAccount
          })
        )
      ]);

      return payee;
    },
    {
      dependencies: [
        queriesPayees.getPayee.queryKeyBase,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayees.listPayees.queryKeyBase,
        queriesPayeeRows.searchPayeeRows.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
      ],
      ...config
    }
  );
};
